import React, { FC } from 'react';
import { Select } from '@kontentino/ui';
import { useUser } from 'modules/user/userSelector';
import WorkflowStatusIcon from './WorkflowStatusIcon';
import { useTranslation } from 'react-i18next';
import { confirmStatusChangeAwayFromPublished } from 'utils/statuses';
import { filterManuallySettableStatuses } from 'app/modules/posts/utils/manuallySettableStatuses';

type Props = {
  pageType: number;
  value: number;
  onChange: (status: number) => void;
  dataCy?: {
    placeholder?: string;
    option?: string;
  };
};

const WorkflowStatusSelector: FC<Props> = ({
  value,
  onChange,
  dataCy,
  pageType,
}) => {
  const { availableStatuses, permissionsKeys, role } = useUser();
  const { t } = useTranslation();

  const statusesOptions = filterManuallySettableStatuses(
    availableStatuses([pageType]),
    permissionsKeys,
    role,
    'postDetail',
  ).map((status) => ({
    value: status,
    label: t(`workflowStatus.label.${status}`),
  }));

  const selectedValueIndex = statusesOptions.findIndex(
    (item) => item.value === value,
  );

  const handleChange = async (nextStatus: number) => {
    const confirmed = await confirmStatusChangeAwayFromPublished(
      value,
      nextStatus,
      t,
    );

    if (!confirmed) {
      return;
    }

    onChange(nextStatus);
  };

  return (
    <div className="tw-w-[280px]" data-cy={dataCy?.placeholder}>
      <Select
        isSearchable={false}
        menuPlacement="top"
        value={statusesOptions[selectedValueIndex]}
        options={statusesOptions}
        onChange={(newValue) =>
          newValue ? handleChange(newValue.value) : null
        }
        components={{
          ValueContainer: ({ children }) => (
            <div className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2  tw-px-4">
              {children}
            </div>
          ),
          SingleValue: (option) => (
            <>
              <WorkflowStatusIcon status={option.data.value} />
              {t(`workflowStatus.label.${option.data.value}`)}
            </>
          ),
        }}
        formatOptionLabel={(option) => (
          <div
            className="tw-flex tw-items-center tw-gap-2"
            data-cy={`${dataCy?.option ?? 'workflow-status-option'}-status-${
              option.value
            }`}
          >
            <WorkflowStatusIcon status={option.value} />
            {t(`workflowStatus.label.${option.value}`)}
          </div>
        )}
      />
    </div>
  );
};

export default WorkflowStatusSelector;
